import { useState } from 'react'
import {
  Card,
  Chip,
  EmptyState,
  LoadingRows,
  ProgressBar,
  StatCard,
  inputClass,
  primaryBtn,
  relativeTime,
  secondaryBtn,
} from '../family-finance/shared'
import type { Destination, DealKind, ScoredDeal } from '../../types/family-planner'
import { deleteDestination, refreshDeals, saveDestination } from '../../lib/family-planner-api'
import { fmtDateRange } from '../../lib/family-planner-analytics'
import { money, type PlannerState } from './usePlanner'

const KIND_META: Record<DealKind, { icon: string; label: string }> = {
  flight: { icon: '✈️', label: 'Flight' },
  cruise: { icon: '🛳️', label: 'Cruise' },
  package: { icon: '🧳', label: 'Package' },
}

export default function TravelTab({ state }: { state: PlannerState }) {
  const { loading, windows, scoredDeals, destinations, deals, members, refresh } = state
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [onlyFitting, setOnlyFitting] = useState(false)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ name: '', region: '', kind: 'flight' as DealKind, budget: '2000', nights: '4' })

  async function guard(key: string, fn: () => Promise<void>) {
    setBusy(key)
    setError(null)
    try {
      await fn()
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  async function addDestination() {
    if (!draft.name.trim()) return
    const nights = Number(draft.nights) || 4
    const destination: Destination = {
      id: `dest-${Date.now()}`,
      name: draft.name.trim(),
      region: draft.region.trim() || 'Anywhere',
      kinds: [draft.kind],
      originAirports: ['AUS', 'DFW'],
      destinationAirports: [],
      maxBudget: Number(draft.budget) || 2000,
      minNights: Math.max(1, nights - 1),
      maxNights: nights + 2,
      enabled: true,
    }
    await guard('add-dest', async () => {
      await saveDestination(destination)
      setAdding(false)
      setDraft({ name: '', region: '', kind: 'flight', budget: '2000', nights: '4' })
    })
  }

  if (loading) return <LoadingRows rows={5} />

  const visibleDeals = onlyFitting ? scoredDeals.filter((d) => d.windowId) : scoredDeals
  const bestDeal = scoredDeals[0]
  const lastChecked = deals.reduce<string | null>((latest, d) => (!latest || d.foundAt > latest ? d.foundAt : latest), null)

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Open windows" value={String(windows.length)} sub="3+ nights, next 75 days" />
        <StatCard
          label="Deals tracked"
          value={String(scoredDeals.length)}
          sub={`checked ${relativeTime(lastChecked)}`}
        />
        <StatCard
          label="Fit the calendar"
          value={String(scoredDeals.filter((d) => d.windowId).length)}
          sub="land inside an open window"
          subTone="green"
        />
        <StatCard
          label="Best find"
          value={bestDeal ? money(bestDeal.price) : '—'}
          sub={bestDeal ? `${Math.round(bestDeal.discount * 100)}% under typical` : 'nothing tracked'}
          subTone={bestDeal && bestDeal.discount > 0 ? 'green' : 'slate'}
        />
      </div>

      <Card
        title="Open windows"
        subtitle="Stretches with nothing (or almost nothing) on the aggregated calendar"
      >
        {windows.length === 0 ? (
          <EmptyState icon="🗓️" message="No clear multi-night stretches in the planning horizon." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {windows.slice(0, 6).map((w) => (
              <div key={w.id} className="border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-900 dark:text-white text-sm font-medium">
                    {fmtDateRange(w.start, w.end)}
                  </span>
                  <Chip tone={w.busyMemberIds.length === 0 ? 'green' : 'amber'}>{w.nights}n</Chip>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                  {w.busyMemberIds.length === 0
                    ? 'Everyone free'
                    : `${w.busyMemberIds
                        .map((id) => members.find((m) => m.id === id)?.name ?? id)
                        .join(', ')} has something scheduled`}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Last-minute deals"
        subtitle="Scored on discount, budget fit, and whether the dates clear the family calendar"
        action={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyFitting}
                onChange={(e) => setOnlyFitting(e.target.checked)}
                className="rounded border-slate-400"
              />
              Only dates that work
            </label>
            <button
              className={secondaryBtn}
              onClick={() => guard('refresh-deals', async () => void (await refreshDeals()))}
              disabled={busy === 'refresh-deals'}
            >
              {busy === 'refresh-deals' ? 'Checking…' : 'Check now'}
            </button>
          </div>
        }
      >
        {visibleDeals.length === 0 ? (
          <EmptyState icon="🧳" message="No deals match right now — try turning off the calendar filter." />
        ) : (
          <div className="space-y-3">
            {visibleDeals.map((deal) => (
              <DealRow key={deal.id} deal={deal} state={state} />
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Watched destinations"
        subtitle="The watcher only prices these — nothing is ever booked automatically"
        action={
          <button className={secondaryBtn} onClick={() => setAdding((a) => !a)}>
            {adding ? 'Cancel' : '+ Add destination'}
          </button>
        }
      >
        {adding && (
          <div className="grid sm:grid-cols-5 gap-3 mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
            <input
              className={inputClass}
              placeholder="Destination"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
            <input
              className={inputClass}
              placeholder="Region"
              value={draft.region}
              onChange={(e) => setDraft({ ...draft, region: e.target.value })}
            />
            <select
              className={inputClass}
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as DealKind })}
            >
              <option value="flight">Flights</option>
              <option value="cruise">Cruises</option>
              <option value="package">Packages</option>
            </select>
            <input
              className={inputClass}
              type="number"
              placeholder="Max budget"
              value={draft.budget}
              onChange={(e) => setDraft({ ...draft, budget: e.target.value })}
            />
            <div className="flex gap-2">
              <input
                className={inputClass}
                type="number"
                placeholder="Nights"
                value={draft.nights}
                onChange={(e) => setDraft({ ...draft, nights: e.target.value })}
              />
              <button className={primaryBtn} onClick={addDestination} disabled={busy === 'add-dest'}>
                Add
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {destinations.map((dest) => (
            <div
              key={dest.id}
              className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-slate-900 dark:text-white text-sm font-medium">{dest.name}</span>
                  {dest.kinds.map((k) => (
                    <Chip key={k} tone="blue">
                      {KIND_META[k].icon} {KIND_META[k].label}
                    </Chip>
                  ))}
                  {!dest.enabled && <Chip tone="slate">paused</Chip>}
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                  {dest.region} · under {money(dest.maxBudget)} · {dest.minNights}–{dest.maxNights} nights
                  {dest.originAirports.length ? ` · from ${dest.originAirports.join('/')}` : ''}
                </p>
                {dest.notes && <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">{dest.notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                <BudgetInput
                  value={dest.maxBudget}
                  onCommit={(next) =>
                    guard(`budget-${dest.id}`, () =>
                      saveDestination({ ...dest, maxBudget: next }).then(() => undefined),
                    )
                  }
                />
                <button
                  className={secondaryBtn}
                  onClick={() =>
                    guard(`toggle-${dest.id}`, () =>
                      saveDestination({ ...dest, enabled: !dest.enabled }).then(() => undefined),
                    )
                  }
                >
                  {dest.enabled ? 'Pause' : 'Resume'}
                </button>
                <button className={secondaryBtn} onClick={() => guard(`del-${dest.id}`, () => deleteDestination(dest.id))}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

/** Budget edits commit on blur so a save isn't fired on every keystroke. */
function BudgetInput({ value, onCommit }: { value: number; onCommit: (next: number) => void }) {
  const [text, setText] = useState(String(value))
  return (
    <input
      className={`${inputClass} w-28`}
      type="number"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const next = Number(text)
        if (Number.isFinite(next) && next !== value) onCommit(next)
        else setText(String(value))
      }}
    />
  )
}

function DealRow({ deal, state }: { deal: ScoredDeal; state: PlannerState }) {
  const meta = KIND_META[deal.kind]
  const destination = state.destinations.find((d) => d.id === deal.destinationId)
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-900 dark:text-white font-medium">
              {meta.icon} {deal.title}
            </span>
            {deal.discount > 0 && <Chip tone="green">{Math.round(deal.discount * 100)}% under typical</Chip>}
            {!deal.withinBudget && <Chip tone="amber">over budget</Chip>}
            {deal.windowId ? (
              <Chip tone="green">dates are clear</Chip>
            ) : deal.blockingEvents.length > 0 ? (
              <Chip tone="red">{deal.blockingEvents.length} conflict{deal.blockingEvents.length === 1 ? '' : 's'}</Chip>
            ) : null}
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
            {destination?.name} · {fmtDateRange(deal.departDate, deal.returnDate)} · {deal.nights} nights ·{' '}
            {deal.provider}
          </p>
          <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">{deal.detail}</p>
          {deal.blockingEvents.length > 0 && (
            <p className="text-amber-600 dark:text-amber-400 text-xs mt-1">
              Would collide with: {deal.blockingEvents.slice(0, 3).map((e) => e.title).join(', ')}
              {deal.blockingEvents.length > 3 ? ` +${deal.blockingEvents.length - 3} more` : ''}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-slate-900 dark:text-white text-xl font-bold">{money(deal.price)}</p>
          <p className="text-slate-400 dark:text-slate-500 text-xs line-through">{money(deal.typicalPrice)}</p>
          <a
            href={deal.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-blue-600 dark:text-blue-400 text-xs hover:underline"
          >
            View on {deal.provider.split(' + ')[0]} →
          </a>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <span className="text-slate-500 dark:text-slate-400 text-xs w-10">{deal.score}/100</span>
        <div className="flex-1">
          <ProgressBar value={deal.score} max={100} tone={deal.score >= 70 ? 'green' : deal.score >= 45 ? 'blue' : 'red'} />
        </div>
      </div>
    </div>
  )
}
